import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Icon, Kbd, LED } from '@/components/ui';

type CommandKind = 'nav' | 'run' | 'system';
interface Command {
  kind: CommandKind;
  label: string;
  hint?: string;
  tag?: string;
  action: () => void;
}

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Global search / command palette triggered by ⌘K · Ctrl+K.
 *
 * Searches across :
 *   - Navigation pages (Dashboard, Runner, Live, History…)
 *   - Recent runs (run_id + system)
 *   - Registered Lab systems
 *
 * Arrow keys navigate, Enter confirms, Escape closes.
 */
export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: runs = [] } = useQuery({
    queryKey: ['runs', { limit: 40 }],
    queryFn: () => api.listRuns({ limit: 40 }),
    enabled: open,
    staleTime: 10_000,
  });
  const { data: systems = [] } = useQuery({
    queryKey: ['systems'],
    queryFn: () => api.systems(),
    enabled: open,
    staleTime: 60_000,
  });

  // Focus + reset on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setCursor(0);
      setTimeout(() => inputRef.current?.focus(), 20);
    }
  }, [open]);

  const commands = useMemo<Command[]>(() => {
    const nav: Command[] = [
      { kind: 'nav', label: 'Dashboard',       hint: 'Accueil',       tag: '/',         action: () => navigate('/') },
      { kind: 'nav', label: 'Lancer une campagne', hint: 'Runner',    tag: '/run',      action: () => navigate('/run') },
      { kind: 'nav', label: 'Suivi en direct', hint: 'Logs temps réel', tag: '/live',   action: () => navigate('/live') },
      { kind: 'nav', label: 'Historique',      hint: 'Runs archivés', tag: '/history', action: () => navigate('/history') },
      { kind: 'nav', label: 'Sentinel',        hint: 'Monitoring passif', tag: '/sentinel', action: () => navigate('/sentinel') },
      { kind: 'nav', label: 'Tests',           hint: 'Catalogue',     tag: '/tests',   action: () => navigate('/tests') },
      { kind: 'nav', label: 'Configuration',   hint: 'Tests & systèmes', tag: '/config', action: () => navigate('/config') },
      { kind: 'nav', label: 'Suites',          hint: 'Gestionnaire',  tag: '/suites',  action: () => navigate('/suites') },
    ];
    const runCmds: Command[] = runs.slice(0, 20).map((r) => ({
      kind: 'run',
      label: r.run_id,
      hint: r.system,
      tag: r.status,
      action: () => navigate(
        r.status === 'running' || r.status === 'stopping'
          ? `/live?run=${r.run_id}`
          : `/results/${r.run_id}`
      ),
    }));
    const sysCmds: Command[] = systems.map((s) => ({
      kind: 'system',
      label: s.id,
      hint: s.host,
      tag: 'système',
      action: () => navigate(`/config?system=${s.id}`),
    }));
    return [...nav, ...sysCmds, ...runCmds];
  }, [runs, systems, navigate]);

  const filtered = useMemo(() => {
    if (!query.trim()) return commands;
    const q = query.toLowerCase();
    return commands.filter((c) =>
      c.label.toLowerCase().includes(q)
      || (c.hint ?? '').toLowerCase().includes(q)
      || (c.tag ?? '').toLowerCase().includes(q),
    );
  }, [commands, query]);

  useEffect(() => { setCursor(0); }, [query]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, filtered.length - 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
      if (e.key === 'Enter') {
        e.preventDefault();
        const cmd = filtered[cursor];
        if (cmd) { cmd.action(); onClose(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, filtered, cursor, onClose]);

  if (!open) return null;

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0,
        zIndex: 1300,
        display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
        paddingTop: 80,
        background: 'rgba(7, 15, 30, 0.82)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        role="dialog"
        aria-label="Rechercher dans la plateforme"
        style={{
          width: 640, maxWidth: '92vw',
          background: 'var(--surface-1)',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--elev-4)',
          overflow: 'hidden',
        }}
      >
        {/* Search input */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '12px 14px',
          borderBottom: '1px solid var(--border-subtle)',
        }}>
          <Icon name="search" size={16} style={{ color: 'var(--text-muted)' }} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher une campagne, un système, une page…"
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-sans)',
              fontSize: 14,
              outline: 'none',
            }}
          />
          <span style={{ display: 'flex', gap: 4 }}>
            <Kbd>esc</Kbd>
          </span>
        </div>

        {/* Results */}
        <div style={{ maxHeight: 420, overflow: 'auto' }}>
          {filtered.length === 0 && (
            <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              Aucun résultat.
            </div>
          )}
          {filtered.map((cmd, i) => {
            const active = i === cursor;
            return (
              <button
                key={`${cmd.kind}:${cmd.label}:${i}`}
                onClick={() => { cmd.action(); onClose(); }}
                onMouseEnter={() => setCursor(i)}
                style={{
                  width: '100%',
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 14px',
                  background: active ? 'var(--accent-soft)' : 'transparent',
                  border: 'none',
                  borderLeft: active ? '2px solid var(--accent)' : '2px solid transparent',
                  color: active ? 'var(--accent)' : 'var(--text-primary)',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <KindIcon kind={cmd.kind} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
                  <span
                    className={cmd.kind === 'run' || cmd.kind === 'system' ? 'mono' : ''}
                    style={{ fontSize: 13, fontWeight: 500 }}
                  >
                    {cmd.label}
                  </span>
                  {cmd.hint && (
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{cmd.hint}</span>
                  )}
                </div>
                {cmd.tag && (
                  <span className="mono" style={{
                    fontSize: 10,
                    color: 'var(--text-muted)',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    padding: '2px 6px',
                    background: 'var(--surface-2)',
                    borderRadius: 'var(--radius-sm)',
                  }}>
                    {cmd.tag}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{
          padding: '8px 14px',
          borderTop: '1px solid var(--border-subtle)',
          display: 'flex', alignItems: 'center', gap: 12,
          fontSize: 11, color: 'var(--text-muted)',
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Kbd>↑</Kbd><Kbd>↓</Kbd> naviguer
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Kbd>↵</Kbd> ouvrir
          </span>
          <div style={{ flex: 1 }} />
          <span>{filtered.length} résultat{filtered.length > 1 ? 's' : ''}</span>
        </div>
      </div>
    </div>
  );
}

function KindIcon({ kind }: { kind: CommandKind }) {
  switch (kind) {
    case 'nav':    return <Icon name="grid"   size={14} style={{ color: 'var(--text-muted)' }} />;
    case 'run':    return <Icon name="activity" size={14} style={{ color: 'var(--info)' }} />;
    case 'system': return <Icon name="server" size={14} style={{ color: 'var(--accent)' }} />;
    default:       return <LED kind="idle" />;
  }
}
